import { ImageSource, ImageSourceSizes } from '@ag1/ui';
import { getImageDimensions } from '@sanity/asset-utils';
import clsx from 'clsx';
import { type CSSProperties, PropsWithChildren, useEffect, useMemo, useRef } from 'react';
import { SanityHotspotImage } from 'schema/studio/common/hotspotImage';
import { urlFor } from 'shared/utils/urlFor';
import { SanityImageAsset, SanityReference } from 'types/studio/studio';
import { getImageCrop, useImageScale } from './imageUtils';
import { useImageOptimization } from './useImageOptimization';
import Image from 'next/image';
import styles from './StudioHotspotImage.module.scss';

type SectionIllustrationProps = PropsWithChildren<{
  className?: string;
  image: SanityHotspotImage;
  style?: CSSProperties; // eslint-disable-line react/no-unused-prop-types
}>;

type StaticPictureProps = PropsWithChildren<{
  className?: string;
}>;

const AdaptivePicture = ({
  image,
  className,
  children,
  width,
  height
}: SectionIllustrationProps & { width: number; height: number }) => {
  const imageCrop = getImageCrop(image, { width, height });
  const setPictureRef = useImageScale(imageCrop);

  const pictureRef = useRef<HTMLPictureElement>(null);

  useEffect(() => {
    if (pictureRef.current) {
      setPictureRef(pictureRef.current);
    }
  }, [setPictureRef, pictureRef]);

  return (
    <picture className={className} ref={pictureRef}>
      {children}
    </picture>
  );
};

const StaticPicture = ({ className, children }: StaticPictureProps) => (
  <picture className={className}>{children}</picture>
);

const StudioHotspotImage = ({
  image,
  className,
  style,
  eagerLoading = false,
  sizes
}: SectionIllustrationProps & { eagerLoading?: boolean; sizes?: ImageSourceSizes }) => {
  const { isInView, containerRef } = useImageOptimization({
    eagerLoading,
    imageUrl: image.url
  });

  const PictureComponent = image.fit === 'adaptive' ? AdaptivePicture : StaticPicture;
  const { width, height } = useMemo(() => getImageDimensions(image), [image]);

  return (
    <div ref={containerRef} className={clsx(styles.illustration, className)} style={style}>
      {isInView && (
        <PictureComponent className={styles.picture} image={image} width={width} height={height}>
          <ImageSource src={image.url} width={width} height={height} sizes={sizes} />

          <Image
            alt={image.alt}
            src={image.url}
            className={clsx(
              styles.image,
              image.fit === 'cover' && styles.imageCover,
              image.position === 'top' && styles.positionTop
            )}
            width={width}
            height={height}
            priority={eagerLoading}
            loading={eagerLoading ? 'eager' : 'lazy'}
            fetchPriority={eagerLoading ? 'high' : 'auto'}
            decoding={eagerLoading ? 'sync' : 'async'}
            draggable={false}
          />
        </PictureComponent>
      )}
    </div>
  );
};

type PropsV2 = PropsWithChildren<{
  className?: string;
  image: {
    asset: SanityReference<SanityImageAsset>;
    alt?: string;
  };
  eagerLoading?: boolean;
  sizes?: ImageSourceSizes;
}>;

export const StudioHotspotImageV2 = ({ image, ...props }: PropsV2) => {
  const formatImage = useMemo(
    () => ({
      ...image,
      ...urlFor(image)
    }),
    [image]
  );
  return <StudioHotspotImage {...props} image={formatImage as SanityHotspotImage} />;
};

export default StudioHotspotImage;
